import { AxiosResponse } from 'axios';
import { bunja, type Scope } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { createContext } from 'react';
import { countGraphemes } from 'unicode-segmenter/grapheme';

import {
  getCommunityFeedsBySymbolApi,
  getLastTradeInfoApi,
  patchCommunityFeedItemApi,
  postCommunityFeedItemApi,
} from '@flipster/apis/v1/community';
import {
  CommunityFeedItemWithSymbol,
  GetCommunityFeedBySymbolResponse,
  LastTradeInfo,
} from '@flipster/apis/v1/community/types';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

import { profileFeedQueryKey } from '../../../profile-page/section/feed-section/bunja';
import { authBunja } from '../../../shared/auth/state';
import { snapshotSpecBunja } from '../../snapshot/state';
import { SymbolScope } from '../../symbol/bunja';

export const maxContentLength = 280;

export const ErrorMessages = {
  'api.community.feed.notEligibleToAddPost': 'You are not eligible to add a post', // non-spec
  'api.community.feed.textContentHasUrl': 'Links can’t be included in posts. Please edit and post.',
  'api.community.feed.blockedWord': 'The post contains restricted words. Edit and retry.',
  'api.community.feed.textTooShort': 'Post cannot be empty',
  'api.community.feed.textTooLong': 'Post too long. Limit is 280 character.',
};

export const FeedItemContext = createContext<CommunityFeedItemWithSymbol>(
  null as unknown as CommunityFeedItemWithSymbol,
);
export const FeedItemScope = createScopeFromContext(FeedItemContext);

type Callback<Value> = (next: Value, prev: Value) => void;
type PostErrorListener = Callback<string | undefined>;
export type SubscribePostError = (listener: PostErrorListener) => () => void;

export const CommunityFeedQueryKey = 'getCommunityFeedsBySymbolApi';
export const InsightsFeedQueryKey = 'getCommunityFeedsApi';

export const feedErrorBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);

  bunja.use(FeedItemScope /** we don't want to reuse this bunja on different feed item */);

  const postErrorBaseAtom = atom<string | undefined>(undefined);
  const postErrorAtom = atom(
    (get) => get(postErrorBaseAtom),
    (get, set, arg: string | undefined) => {
      const prev = get(postErrorBaseAtom);
      set(postErrorBaseAtom, arg);
      const next = get(postErrorBaseAtom);
      get(postErrorListenersAtom).forEach((callback) => callback(next, prev));
    },
  );
  const postErrorListenersAtom = atom<PostErrorListener[]>([]);
  function subscribePostError(listener: PostErrorListener) {
    store.set(postErrorListenersAtom, (prev) => [...prev, listener]);
    return function unsubscribe() {
      return store.set(postErrorListenersAtom, (prev) => {
        const index = prev.indexOf(listener);
        return [...prev.slice(0, index), ...prev.slice(index + 1)];
      });
    };
  }
  return { postErrorAtom, subscribePostError };
});

export const feedPostAndEditBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const symbol = bunja.use(SymbolScope);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const item = bunja.use(FeedItemScope as Scope<CommunityFeedItemWithSymbol | null>);
  const { postErrorAtom, subscribePostError } = bunja.use(feedErrorBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const isEditing = item != null;

  const symbolAtom = atom((get) => {
    if (!symbol) return '';
    return get(instrumentsKeyBySymbolAtom)?.[symbol]?.displayShortName ?? '';
  });

  // Eligibility
  const eligibilityBaseAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    return {
      queryKey: [CommunityFeedQueryKey, isLoggedIn, symbol, 'eligibility'],
      queryFn: () =>
        getCommunityFeedsBySymbolApi(symbol, {
          count: 0,
          showEligibility: true,
          showAdditionalData: false,
        }),
      enabled: symbol != null && isLoggedIn,
      select: (response: AxiosResponse<GetCommunityFeedBySymbolResponse>) => response.data.postWriteEligibility,
      refetchOnMount: true,
    };
  });
  const ineligibilityAtom = atom((get) => {
    if (isEditing) return;
    const { data } = get(eligibilityBaseAtom);
    if (!data?.hasValidKyc) return { message: 'You can post 24 hours after KYC approval.' };
    if (!data?.tradedWithin7d)
      return { message: `You need a ${get(symbolAtom)} Perpetual trade in the last 7 days to post.` };
  });

  // Post content
  const textContentBaseAtom = atom(item?.textContent ?? '');
  const textContentAtom = atom(
    // TODO: wrap all trade page with SymbolContext and re-init bunja everytime symbol changes.
    (get) => (get(ineligibilityAtom) ? '' : get(textContentBaseAtom)),
    (get, set, next: string) => {
      if (get(ineligibilityAtom)) return;
      set(postErrorAtom, undefined);
      set(textContentBaseAtom, next);
    },
  );
  const textContentLengthAtom = atom((get) => countGraphemes(get(textContentAtom)));
  const postFeedContentMutationAtom = atomWithMutation((get) => ({
    mutationKey: ['postCommunityFeedItemApi'],
    mutationFn: () => {
      const latestTrade = get(lastTradeInfoAtom);
      return postCommunityFeedItemApi(symbol, {
        textContent: get(textContentAtom).trim(),
        positionId: latestTrade?.positionId,
        positionCardType: latestTrade?.positionCardType,
      });
    },
  }));
  const editFeedContentMutationAtom = atomWithMutation((get) => ({
    enabled: item != null,
    mutationKey: ['patchCommunityFeedItemApi'],
    mutationFn: () => {
      const latestTrade = get(lastTradeInfoAtom);
      return patchCommunityFeedItemApi(item!.symbol, item!.feedId, {
        textContent: get(textContentAtom).trim(),
        deletePositionCard: !latestTrade,
      });
    },
  }));

  // Last trade
  const lastTradeInfoAtom = atom<LastTradeInfo | undefined>(
    item?.positionCard
      ? ({
          positionId: '',
          positionCardType: item.positionCardType,
          positionCard: item.positionCard,
        } as LastTradeInfo)
      : undefined,
  );
  function clearLastTrade() {
    store.set(lastTradeInfoAtom, undefined);
  }
  async function fetchLastTrade() {
    if (isEditing) return;
    if (!store.get(isLoggedInAtom)) return snackbar.error({ message: 'Sign in to join the feed' });
    if (!symbol) return;
    try {
      const response = await getLastTradeInfoApi(symbol);
      store.set(lastTradeInfoAtom, response.data.lastTradeInfo);
    } catch (error) {
      return snackbar.error({ message: getErrorMessage(error) });
    }
  }

  async function postFeedContent(onSumbitComplete: () => void /* please forgive me */) {
    const textContent = store.get(textContentAtom);
    const lastTradeInfo = store.get(lastTradeInfoAtom);
    segment.track(CommunityEvent.SharePostClick, {
      latest_trade: Boolean(lastTradeInfo),
      position_id: lastTradeInfo?.positionId,
      upload_type: isEditing ? 'edited' : 'new',
      post_message: textContent,
    });
    if (!store.get(isLoggedInAtom)) return snackbar.error({ message: 'Sign in to join the feed' });
    if (textContent.trim().length == 0)
      return snackbar.error({ message: ErrorMessages['api.community.feed.textTooShort'] });
    const { mutateAsync } = store.get(isEditing ? editFeedContentMutationAtom : postFeedContentMutationAtom);
    try {
      await mutateAsync();
      snackbar.success({ message: isEditing ? 'Your post has been updated' : 'Your post is now live' });
      await clearAfterPost();
      onSumbitComplete();
    } catch (e) {
      const errorMessage = getErrorMessage(e);
      const errorCode = getErrorCode(e);
      switch (errorCode) {
        case 'api.positionAlreadyClosed':
          snackbar.error({ message: errorMessage });
          store.set(lastTradeInfoAtom, undefined);
          break;
        case 'api.community.feed.notEligibleToAddPost': {
          snackbar.error({ message: errorMessage });
          const queryClient = store.get(queryClientAtom);
          queryClient.invalidateQueries({ queryKey: [CommunityFeedQueryKey] });
          break;
        }
        case 'api.community.feed.textContentHasUrl':
        case 'api.community.feed.blockedWord':
        case 'api.community.feed.textTooShort':
        case 'api.community.feed.textTooLong':
          store.set(postErrorAtom, errorMessage);
          break;
        default:
          snackbar.error({ message: errorMessage });
      }
    }
    async function clearAfterPost() {
      const queryClient = store.get(queryClientAtom);
      await queryClient.invalidateQueries({ queryKey: [CommunityFeedQueryKey] });
      await queryClient.invalidateQueries({ queryKey: [InsightsFeedQueryKey] });
      await queryClient.invalidateQueries({ queryKey: [profileFeedQueryKey] });
      store.set(textContentAtom, '');
      store.set(lastTradeInfoAtom, undefined);
    }
  }
  return {
    isEditing,
    isLoggedInAtom,
    ineligibilityAtom,
    textContentAtom,
    textContentLengthAtom,
    postErrorAtom,
    lastTradeInfoAtom,
    addLastTrade: fetchLastTrade,
    clearLastTrade,
    subscribePostError,
    submit: postFeedContent,
  };
});
